import { usePromisedRequest as promiseRequest } from "../Common/request";
import { resolveNoContent, resolveWithBody } from "./Utils/utils";

export type PredefinedNotificationType = "paymentReminder" | "singleCompetitorCategories";

export type PredefinedNotificationVariant = "changeOrRefund" | "changeOnly";

export type ReasonNotPossible =
    | "noEmailContactData"
    | "noRegistrationStage"
    | "noReceivers"
    | "locked"
    | "alreadySent"
    | "noDeadline"
    | "registrationOpen"
    | "noSingleCompetitorCategories"
    | "startingListsNotShared";

export type PredefinedNotificationRequestOptions = {
    variant?: PredefinedNotificationVariant;
};

export type PreviousPredefinedNotificationRequest = {
    /** UTC, yyyy-MM-dd HH:mm:ss */
    requestedAt: string;
    requestedBy: string;
};

type BasePredefinedNotification = {
    possible: boolean;
    content: string | null;
    receivers: {
        users: number;
        phones: number;
        emails: number;
    };
    calculatedCost: {
        formattedTotalPrice: string;
        formattedPriceForType: string;
        numberOfReceivers: number;
    };
    originalFormattedPriceForType: string | null;
    reasonNotPossible: ReasonNotPossible | null;
    previousRequest: PreviousPredefinedNotificationRequest | null;
};

export type PaymentReminderNotification = BasePredefinedNotification & {
    type: "paymentReminder";
};

export type SingleCompetitorCategoriesDeadline = {
    /** UTC. The saved setting when the notification is ready, otherwise null. */
    until: string | null;
    /** UTC. Now plus the minimum margin, the lower limit of the enable step. */
    earliest: string;
    /** UTC. Event start date, the upper limit of the enable step. */
    latest: string | null;
};

export type SingleCompetitorCategoriesNotification = BasePredefinedNotification & {
    type: "singleCompetitorCategories";
    /** Missing when the response comes from the generic resolver (e.g. already sent). */
    deadline?: SingleCompetitorCategoriesDeadline | null;
    examplePreview?: string | null;
};

export type PredefinedNotification = PaymentReminderNotification | SingleCompetitorCategoriesNotification;

export default {
    getData(
        type: PredefinedNotificationType,
        eventId: number,
        options: PredefinedNotificationRequestOptions = {},
    ): Promise<PredefinedNotification> {
        return promiseRequest<PredefinedNotification>({
            method: "post",
            url: `/api/events/${eventId}/predefined-notifications/data`,
            json: { type, ...options },
        }).then(resolveWithBody());
    },

    push(type: PredefinedNotificationType, eventId: number, options: PredefinedNotificationRequestOptions = {}): Promise<void> {
        return promiseRequest({
            method: "post",
            url: `/api/events/${eventId}/predefined-notifications/push`,
            json: { type, ...options },
        }).then(resolveNoContent());
    },
};
